// Support ticket modal — dumb-simple on purpose: the user types a subject and
// what happened; the client silently attaches the diagnostic context
// (current page, breadcrumb trail of recent navigation/API activity, browser,
// build) via buildSupportContext so platform support can reproduce the issue
// without a back-and-forth. The context is never shown here — admin console
// only.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useAuth } from '../../hooks/useAuth.js';
import { buildSupportContext } from '../../lib/breadcrumbs.js';
import AssistantChat from './AssistantChat.jsx';

const STATUS_STYLE = {
  open:        'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-700',
  closed:      'bg-gray-100 text-gray-500',
};
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', closed: 'Closed' };

// HOW MUCH OF AN ATTACHMENT SURVIVES. The worker caps `context_json` at 32KB and — importantly —
// replaces the WHOLE blob with a stub when it overflows, so a large trace would take the breadcrumbs
// down with it. Trim here instead, and say what was trimmed: a 290-row trace is ~30KB on its own.
const ATTACH_BUDGET_BYTES = 22 * 1024;

function fitAttachment(attachment) {
  if (!attachment) return { attachment: null, omitted: 0 };
  const rows = Array.isArray(attachment.rows) ? attachment.rows : [];
  const meta = { ...attachment, rows: [] };
  let used = JSON.stringify(meta).length;
  const kept = [];
  // Newest first: the end of a trace is the part that explains what just happened.
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const cost = JSON.stringify(rows[i]).length + 1;
    if (used + cost > ATTACH_BUDGET_BYTES) break;
    used += cost;
    kept.unshift(rows[i]);
  }
  return { attachment: { ...attachment, rows: kept }, omitted: rows.length - kept.length };
}

/**
 * `attachment` — optional structured evidence to carry into the ticket, e.g. a call trace from a
 * line's Diagnostics tab. Kevin, 2026-08-22: "add the ability to submit a ticket with the call trace
 * attached." It rides in the hidden diagnostic context, which is what that field is for, and the
 * modal SAYS it is attached: silently posting somebody's SIP signalling would be the wrong default
 * even when the destination is our own platform admin.
 */
export default function SupportModal({
  onClose, initialSubject = '', initialDescription = '', attachment = null, warning = null,
}) {
  const { role } = useAuth();
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState(initialSubject);
  const [description, setDescription] = useState(initialDescription);
  const fitted = fitAttachment(attachment);
  const [submittedId, setSubmittedId] = useState(null);
  // ASK FIRST, FILE SECOND — which is the order these actually happen in. An operator asks "why
  // isn't this registered" and only then decides whether it needs a human. Opening on the ticket
  // form when the answer might be one question away made the bot invisible.
  //
  // Unless the caller arrived WITH something to report: a prefilled subject or an attached call
  // trace means they came to file, so do not make them find the tab.
  const cameToFile = Boolean(initialSubject || initialDescription || attachment);
  const [tab, setTab] = useState(cameToFile ? 'ticket' : 'ask');

  const ticketsQ = useQuery({
    queryKey: ['support-tickets'],
    queryFn: () => api.get('/support-tickets'),
  });

  const submit = useMutation({
    mutationFn: () => api.post('/support-tickets', {
      subject,
      description,
      context: {
        ...buildSupportContext({ role }),
        ...(fitted.attachment
          ? { attachment: fitted.attachment, attachment_rows_omitted: fitted.omitted }
          : {}),
      },
    }),
    onSuccess: (data) => {
      setSubmittedId(data.id);
      setSubject('');
      setDescription('');
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    },
  });

  const tickets = ticketsQ.data?.tickets || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Help</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              {tab === 'ask'
                ? 'Ask about this org\u2019s phone service, or raise a ticket if it needs a human.'
                : 'Tell us what went wrong \u2014 we\u2019ll take it from there.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">✕</button>
        </div>

        <div className="mt-3 flex gap-1 border-b border-gray-200" role="tablist">
          {[['ask', 'Ask Verso'], ['ticket', 'Raise a ticket']].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`-mb-px rounded-t-md border-b-2 px-3 py-1.5 text-sm ${
                tab === id
                  ? 'border-blue-600 font-semibold text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'ask' ? (
          <AssistantChat page={typeof window !== 'undefined' ? window.location.pathname : null} />
        ) : submittedId ? (
          <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            Ticket <span className="font-mono">#{submittedId}</span> submitted — support will follow up.
            <button onClick={() => setSubmittedId(null)}
              className="ml-2 text-green-700 underline hover:text-green-900">Submit another</button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">What&rsquo;s the issue?</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Voicemail email never arrived"
                maxLength={200}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">What happened? <span className="font-normal text-gray-400">(optional)</span></label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="What were you trying to do, and what did you see instead?"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            {fitted.attachment && (
              // Named, counted, and never silent. The operator is sending this on; they should be
              // able to see what "attached" means before they press the button.
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                <span className="font-medium">Attached: {fitted.attachment.label}</span>
                {typeof fitted.attachment.matched === 'number' && (
                  <> — {fitted.attachment.rows.length} of {fitted.attachment.matched} messages</>
                )}
                {fitted.omitted > 0 && (
                  <> ({fitted.omitted} older message{fitted.omitted === 1 ? '' : 's'} trimmed to fit)</>
                )}
                {fitted.attachment.other_extensions?.length > 0 && (
                  <div className="mt-0.5">
                    Includes traffic from other extensions behind the same address
                    ({fitted.attachment.other_extensions.join(', ')}).
                  </div>
                )}
                {warning && (
                  // An attachment that contains nothing useful is worse than none: it looks like
                  // evidence. Said here, where there is still time not to send it.
                  <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900">
                    {warning}
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400">
              Technical details about your current page and recent activity are attached
              automatically so support can reproduce the issue. No message content or
              passwords are included.
            </p>
            {submit.isError && (
              <p className="text-sm text-red-600">{submit.error?.body?.error || submit.error?.message || 'Submit failed'}</p>
            )}
            <button
              onClick={() => submit.mutate()}
              disabled={!subject.trim() || submit.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submit.isPending ? 'Submitting…' : 'Submit ticket'}
            </button>
          </div>
        )}

        {tickets.length > 0 && (
          <div className="mt-6">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Your recent tickets</h4>
            <div className="mt-2 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {tickets.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[t.status] || 'bg-gray-100 text-gray-500'}`}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                  <span className="flex-1 truncate text-gray-700" title={t.subject}>{t.subject}</span>
                  <span className="whitespace-nowrap text-xs text-gray-400">
                    {new Date(`${String(t.created_at).replace(' ', 'T')}Z`).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
